import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Logo } from '../components/Logo';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { 
  Leaf, Users, FileText, AlertTriangle, Pill, 
  Loader2, Search, ChevronRight, Calendar, Mail, Clock, CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProviderPortalPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState('login'); // 'login', 'register', 'dashboard', 'pending'
  const [isLoading, setIsLoading] = useState(false);
  const [provider, setProvider] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('provider_token'));
  const [patients, setPatients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [notes, setNotes] = useState('');

  // Form states
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [specialty, setSpecialty] = useState('');
  const [licenseNumber, setLicenseNumber] = useState('');

  useEffect(() => {
    if (token) {
      fetchPatients();
      setMode('dashboard');
    }
  }, [token]);

  const fetchPatients = async () => {
    try {
      const res = await axios.get(`${API}/provider/patients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPatients(res.data.patients);
    } catch (error) {
      console.error('Error fetching patients:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('provider_token');
        setToken(null);
        setMode('login');
      }
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const res = await axios.post(`${API}/provider/login`, { email, password });
      const providerData = res.data.provider;
      
      // Check if provider is admin verified
      if (!providerData.verified) {
        setProvider(providerData);
        setMode('pending');
        toast.info('Your account is pending admin verification');
        return;
      }
      
      localStorage.setItem('provider_token', res.data.access_token);
      setToken(res.data.access_token);
      setProvider(providerData);
      setMode('dashboard');
      toast.success('Logged in successfully');
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail === 'Please verify your email first') {
        toast.error('Please verify your email first');
      } else {
        toast.error(detail || 'Login failed');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      await axios.post(`${API}/provider/register`, {
        email,
        password,
        name,
        specialty,
        license_number: licenseNumber
      });
      toast.success('Account created! Please log in.');
      setMode('login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveNotes = async () => {
    if (!selectedPatient) return;
    
    try {
      await axios.post(
        `${API}/provider/notes/${selectedPatient.assessment_id}`,
        { notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Notes saved');
      fetchPatients();
    } catch (error) {
      toast.error('Failed to save notes');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('provider_token');
    setToken(null);
    setProvider(null);
    setMode('login');
  };

  const filteredPatients = patients.filter(p => 
    p.patient_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.patient_email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-[#1A1A1A]">
            <Logo size="default" linkTo={null} className="pointer-events-none" />
            <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full ml-2">
              Provider Portal
            </span>
          </Link>
          {mode === 'dashboard' && (
            <Button variant="ghost" onClick={handleLogout}>
              Logout
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {mode === 'pending' ? (
          <div className="max-w-md mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl shadow-sm border border-border p-8"
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-8 h-8 text-amber-600" />
                </div>
                <h1 className="text-2xl font-semibold text-[#1A1A1A]">Pending Verification</h1>
                <p className="text-muted-foreground mt-2">
                  Your account is awaiting admin approval
                </p>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">What happens next?</p>
                    <ul className="text-sm text-amber-700 mt-2 space-y-1">
                      <li>• Our admin team will review your credentials</li>
                      <li>• You'll receive an email once approved</li>
                      <li>• This typically takes 1-2 business days</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircle2 className="w-4 h-4" />
                  <span>Email verified</span>
                </div>
                <div className="flex items-center gap-2 text-amber-600">
                  <Clock className="w-4 h-4" />
                  <span>Admin verification pending</span>
                </div>
              </div>
              
              <Button 
                variant="outline" 
                className="w-full mt-6"
                onClick={() => {
                  setMode('login');
                  setProvider(null);
                }}
              >
                Back to Login
              </Button>
            </motion.div>
          </div>
        ) : mode !== 'dashboard' ? (
          <div className="max-w-md mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl shadow-sm border border-border p-8"
            >
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-primary" />
                </div>
                <h1 className="text-2xl font-semibold text-[#1A1A1A]">
                  {mode === 'login' ? 'Provider Login' : 'Register as Provider'}
                </h1>
                <p className="text-muted-foreground mt-2">
                  Access member supplement protocols shared with you
                </p>
              </div>

              <form onSubmit={mode === 'login' ? handleLogin : handleRegister} className="space-y-4">
                {mode === 'register' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium mb-2">Full Name</label>
                      <Input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Dr. Jane Smith"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Specialty</label>
                      <Input
                        value={specialty}
                        onChange={(e) => setSpecialty(e.target.value)}
                        placeholder="e.g., Internal Medicine"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">License Number</label>
                      <Input
                        value={licenseNumber}
                        onChange={(e) => setLicenseNumber(e.target.value)}
                        placeholder="Optional"
                      />
                    </div>
                  </>
                )}
                
                <div>
                  <label className="block text-sm font-medium mb-2">Email</label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="provider@clinic.com"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Password</label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    minLength={8}
                  />
                </div>

                <Button type="submit" disabled={isLoading} className="w-full btn-primary">
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : mode === 'login' ? (
                    'Sign In'
                  ) : (
                    'Create Account'
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <button
                  onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
                  className="text-sm text-primary hover:underline"
                >
                  {mode === 'login' 
                    ? "Don't have an account? Register" 
                    : "Already have an account? Sign in"}
                </button>
              </div>
            </motion.div>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {/* Member List */}
            <div className="md:col-span-1">
              <div className="bg-white rounded-2xl border border-border p-4 sticky top-24">
                <h2 className="font-semibold text-lg mb-4">Shared Members</h2>
                
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search members..."
                    className="pl-10"
                  />
                </div>

                {filteredPatients.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No members yet</p>
                    <p className="text-xs mt-1">
                      Members will appear here when they share their protocols with you
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                    {filteredPatients.map((patient) => (
                      <button
                        key={patient.share_id}
                        onClick={() => {
                          setSelectedPatient(patient);
                          setNotes(patient.provider_notes || '');
                        }}
                        className={`w-full text-left p-3 rounded-xl transition-colors ${
                          selectedPatient?.share_id === patient.share_id
                            ? 'bg-primary/10 border border-primary/20'
                            : 'bg-muted/50 hover:bg-muted'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <p className="font-medium truncate">{patient.patient_name}</p>
                          <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          Shared {formatDate(patient.shared_at)}
                        </p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Member Details */}
            <div className="md:col-span-2">
              {!selectedPatient ? (
                <div className="bg-white rounded-2xl border border-border p-8 text-center">
                  <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h2 className="text-xl font-semibold mb-2">Select a Member</h2>
                  <p className="text-muted-foreground">
                    Click on a member to view their supplement protocol
                  </p>
                </div>
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="space-y-6"
                >
                  {/* Member Header */}
                  <div className="bg-white rounded-2xl border border-border p-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <h2 className="text-xl font-semibold">{selectedPatient.patient_name}</h2>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                          <Mail className="w-4 h-4" />
                          <span>{selectedPatient.patient_email}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                          <Calendar className="w-4 h-4" />
                          <span>Shared on {formatDate(selectedPatient.shared_at)}</span>
                        </div>
                      </div>
                      <Link to={`/shared/${selectedPatient.assessment_id}`} target="_blank">
                        <Button variant="outline" size="sm">
                          View Full Report
                        </Button>
                      </Link>
                    </div>
                  </div>

                  {/* Medications & Interactions */}
                  {(selectedPatient.medications?.length > 0 || selectedPatient.drug_interactions?.length > 0) && (
                    <div className="bg-white rounded-2xl border border-border p-6">
                      <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                        <Pill className="w-5 h-5 text-primary" />
                        Medications & Interactions
                      </h3>
                      
                      {selectedPatient.medications?.length > 0 && (
                        <div className="mb-4">
                          <p className="text-sm font-medium mb-2">Current Medications:</p>
                          <div className="flex flex-wrap gap-2">
                            {selectedPatient.medications.map((med, i) => (
                              <span key={i} className="px-3 py-1 bg-muted rounded-full text-sm">
                                {med}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {selectedPatient.drug_interactions?.length > 0 && (
                        <div>
                          <p className="text-sm font-medium mb-2 flex items-center gap-2 text-amber-600">
                            <AlertTriangle className="w-4 h-4" />
                            Medication-Supplement Interactions:
                          </p>
                          <div className="space-y-2">
                            {selectedPatient.drug_interactions.map((inter, i) => (
                              <div 
                                key={i}
                                className={`p-3 rounded-lg text-sm ${
                                  inter.interaction_type === 'avoid' 
                                    ? 'bg-red-50 border border-red-200'
                                    : inter.interaction_type === 'caution'
                                    ? 'bg-amber-50 border border-amber-200'
                                    : 'bg-blue-50 border border-blue-200'
                                }`}
                              >
                                <p className="font-medium">
                                  {inter.medication} + {inter.supplement}
                                </p>
                                <p className="text-muted-foreground mt-1">{inter.description}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Health Scores */}
                  <div className="bg-white rounded-2xl border border-border p-6">
                    <h3 className="font-semibold text-lg mb-4">Health Scores</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {Object.entries(selectedPatient.health_scores || {})
                        .sort((a, b) => a[1] - b[1])
                        .map(([category, score]) => (
                          <div 
                            key={category}
                            className="p-3 bg-muted/50 rounded-xl text-center"
                          >
                            <p className={`text-xl font-bold ${
                              score >= 80 ? 'text-green-600' :
                              score >= 60 ? 'text-amber-600' : 'text-red-600'
                            }`}>
                              {score}%
                            </p>
                            <p className="text-xs text-muted-foreground capitalize">
                              {category.replace(/_/g, ' ')}
                            </p>
                          </div>
                        ))}
                    </div>
                  </div>

                  {/* Provider Notes */}
                  <div className="bg-white rounded-2xl border border-border p-6">
                    <h3 className="font-semibold text-lg mb-4">Your Notes</h3>
                    <Textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Add your clinical notes, recommendations, or follow-up items..."
                      className="min-h-[120px] mb-4"
                    />
                    <Button onClick={handleSaveNotes} className="btn-primary">
                      Save Notes
                    </Button>
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
